import { ImmutableObject } from "@hookstate/core"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import InfoIcon from "@mui/icons-material/Info"
import {
  CardActions,
  CardContent,
  CircularProgress,
  Skeleton,
} from "@mui/material"
import { useRouter } from "next/router"

import { FC, useEffect, useMemo, useState } from "react"

import { Button } from "src/components/common/button"
import { Card } from "src/components/common/card"
import { create } from "src/helpers/bem"
import { getPage } from "src/helpers/pages"
import { formatPrice } from "src/helpers/price"
import { useTranslation } from "src/hooks/translation"

import { useBookings } from "src/states/bookings"
import { mergeDialog } from "src/states/dialogs"
import { useLocale } from "src/states/locale"
import {
  CartProduct,
  getCartProductAmount,
  postRefreshCart,
  setCart,
  useCart,
} from "src/states/shop/carts"

import { PagesProps } from "src/types/SharedPageProps"
import { xRMApiTermsOfService } from "src/types/xRM"

import { CCInformation } from "./ShopCartOverview"
import { BillingOptions } from "./ShopCartOverviewBillingOptionsCard"
import styles from "./ShopCartOverviewSummary.module.scss"

const bem = create(styles, "ShopCartOverviewSummary")

export type ShopCartOverviewSummaryProps = {
  billingOptions: BillingOptions
  needsCCInformation: boolean
  ccInformation: CCInformation
  pages: PagesProps
  termsOfServices: xRMApiTermsOfService[]
  checkoutProducts: ImmutableObject<CartProduct[]>
  onCheckout: () => Promise<void>
}

export const ShopCartOverviewSummary: FC<ShopCartOverviewSummaryProps> = ({
  billingOptions,
  pages,
  termsOfServices,
  checkoutProducts,
  needsCCInformation,
  ccInformation,
  onCheckout,
}) => {
  const locale = useLocale()
  const router = useRouter()
  const translation = useTranslation()
  const cart = useCart({ id: router.query.cartId as string })
  const { bookings } = useBookings()
  const m = translation.messages.pages.shop.cart.checkout.summary
  const [loading, setLoading] = useState(false)
  const [checkoutLoading, setCheckoutLoading] = useState(false)
  const [termsOfServicesAccepted, setTermsOfServicesAccepted] = useState(false)

  useEffect(() => {
    setTermsOfServicesAccepted(
      termsOfServices
        .filter(
          (t) => t.termsOfService?.urlType === "GeneralTermsAndConditions",
        )
        .every((t) => t.value),
    )
  }, [termsOfServices])

  useEffect(() => {
    setLoading(true)
    setTermsOfServicesAccepted(false)
    const refreshCartAsync = async () =>
      setCart(await postRefreshCart(cart), true)
    refreshCartAsync().finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const webshopOrdersPage = useMemo(
    () => getPage("Webshop Incidents Template", locale, pages),
    [pages, locale],
  )

  const bookingNumber = bookings.find((booking) => booking.id === cart.id)?.name

  const total = useMemo(
    () =>
      formatPrice(
        checkoutProducts.reduce(
          (sum, product) =>
            sum +
            product.productVariant?.basePrice! * getCartProductAmount(product) +
            (product.productVariant?.mandatoryProducts?.reduce(
              (sum, mandatory) =>
                sum + (mandatory.basePrice ?? 0) * (mandatory.quantity ?? 1),
              0,
            ) ?? 0),
          0,
        ),
        locale,
      ),
    [checkoutProducts, locale],
  )

  const cartEmpty = checkoutProducts.length < 1
  const hasBillingAddress =
    billingOptions.permission?.debitorName != null ||
    billingOptions.permission?.debitorForeignName != null
  const disabledBillingAdress = billingOptions.orderOnOwnAccount
    ? billingOptions.permission?.debitorName == null
    : billingOptions.permission?.debitorForeignName == null
  const hasEInvoice = billingOptions.email && billingOptions.emailIsValid
  const hasCCInformation =
    ccInformation.name &&
    ccInformation.number &&
    ccInformation.expirationDate &&
    ccInformation.cvv

  return (
    <Card>
      <CardContent>
        <div className={bem()}>
          <div className={bem("booking")}>
            <div>{m.booking}</div>
            <div>{bookingNumber}</div>
          </div>
          <div className={bem("total")}>
            <div>{m.total}</div>
            <div className={bem("total__result")}>
              {loading ? <Skeleton height={30} width={100} /> : total}
            </div>
          </div>
          {billingOptions.orderOnOwnAccount && (
            <div
              className={bem("condition", {
                "is-disabled": !hasEInvoice,
              })}
            >
              {hasEInvoice ? <CheckCircleIcon /> : <InfoIcon />}
              <div>
                {hasEInvoice ? (
                  <>
                    {m.conditions.invoiceMail}
                    {billingOptions.email}
                  </>
                ) : (
                  <>{m.conditions.noInvoiceMail}</>
                )}
              </div>
            </div>
          )}
          {needsCCInformation && (
            <div
              className={bem("condition", {
                "is-disabled": !hasCCInformation,
              })}
            >
              {hasCCInformation ? <CheckCircleIcon /> : <InfoIcon />}
              <div>
                {hasCCInformation ? (
                  <>{m.conditions.ccInformation}</>
                ) : (
                  <>{m.conditions.noCCInformation}</>
                )}
              </div>
            </div>
          )}
          <div
            className={bem("condition", {
              "is-disabled": disabledBillingAdress,
            })}
          >
            {hasBillingAddress ? (
              !billingOptions.orderOnOwnAccount &&
              billingOptions.permission?.debitorForeignName != null ? (
                <>
                  <CheckCircleIcon />
                  <div>
                    {translation.formatMessage(m.conditions.billing, {
                      name: billingOptions.permission.debitorForeignName,
                    })}
                  </div>
                </>
              ) : !billingOptions.orderOnOwnAccount &&
                billingOptions.permission?.debitorForeignName == null ? (
                <>
                  <InfoIcon />
                  <div>{m.conditions.noBilling.foreign}</div>
                </>
              ) : billingOptions.orderOnOwnAccount &&
                billingOptions.permission?.debitorName != null ? (
                <>
                  <CheckCircleIcon />
                  <div>
                    {translation.formatMessage(m.conditions.billing, {
                      name: billingOptions.permission.debitorName,
                    })}
                  </div>
                </>
              ) : (
                <>
                  <InfoIcon className={bem("no-billing")} />
                  <div className={bem("no-billing")}>
                    {m.conditions.noBilling.own}
                  </div>
                </>
              )
            ) : (
              <>
                {billingOptions.orderOnOwnAccount &&
                billingOptions.permission?.debitorName == null ? (
                  <>
                    <InfoIcon className={bem("no-billing")} />
                    <div className={bem("no-billing")}>
                      {m.conditions.noBilling.own}
                    </div>
                  </>
                ) : (
                  <>
                    {!billingOptions.orderOnOwnAccount &&
                    billingOptions.permission?.debitorForeignName == null ? (
                      <>
                        <InfoIcon />
                        <div>{m.conditions.noBilling.foreign}</div>
                      </>
                    ) : (
                      ""
                    )}
                  </>
                )}
              </>
            )}
          </div>
          <div className={bem("condition", "is-disabled")}>
            <InfoIcon />
            <div>{m.conditions.termsOfServices.invoiceRebookings}</div>
          </div>
          {termsOfServices.length > 0 ? (
            <div
              className={bem("condition", {
                "is-disabled": !termsOfServicesAccepted,
              })}
            >
              {termsOfServicesAccepted ? <CheckCircleIcon /> : <InfoIcon />}
              <div>
                {termsOfServicesAccepted
                  ? m.conditions.termsOfServices.met
                  : m.conditions.termsOfServices.unmet}
              </div>
            </div>
          ) : (
            ""
          )}
        </div>
      </CardContent>
      <CardActions className={bem("actions")}>
        {loading ? (
          <Skeleton height={40} width={"100%"} />
        ) : checkoutLoading ? (
          <CircularProgress />
        ) : (
          <Button
            variant="contained"
            disabled={
              cartEmpty ||
              !termsOfServicesAccepted ||
              disabledBillingAdress ||
              (!hasEInvoice && billingOptions.orderOnOwnAccount)
            }
            title={
              cart.products.length === checkoutProducts.length
                ? m.checkout
                : m.partialCheckout
            }
            onClick={async () => {
              try {
                setCheckoutLoading(true)
                await onCheckout()
                mergeDialog("shopOrderOverviewRedirectDialog", {
                  open: true,
                })
              } finally {
                setCheckoutLoading(false)
              }
            }}
          />
        )}
      </CardActions>
    </Card>
  )
}
